// Server onboarding, for admins (ADMIN_DISCORD_IDS). Add the bot to a server with the invite
// link, then onboard it by choosing which role marks its Storytellers.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, TriangleAlert } from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../state/auth.js";
import { Button, Dialog, DialogContent } from "../components/ui/primitives.js";
import { PageShell } from "../components/ui/PageShell.js";
import { GuildIcon } from "../components/ui/GuildSelect.js";

type AdminGuild = {
  id: string;
  name: string;
  iconUrl: string | null;
  botPresent: boolean;
  /** Null until the server is onboarded. */
  storytellerRoleId: string | null;
};
type Role = { id: string; name: string };

const selectClass =
  "min-w-0 flex-1 rounded-md border border-ink-line bg-ink px-2 py-1 text-sm text-parchment focus:border-brass/60 focus:outline-none";

function GuildRow({ guild, onChanged }: { guild: AdminGuild; onChanged: () => Promise<void> }) {
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [roleId, setRoleId] = useState(guild.storytellerRoleId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOffboard, setConfirmOffboard] = useState(false);

  const onboarded = guild.storytellerRoleId !== null;

  useEffect(() => {
    if (!guild.botPresent) return;
    api<{ roles: Role[] }>(`/api/admin/guilds/${guild.id}/roles`)
      .then((body) => setRoles(body.roles))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Could not load roles."));
  }, [guild.id, guild.botPresent]);

  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(() => api(`/api/admin/guilds/${guild.id}`, { method: "PUT", body: { storytellerRoleId: roleId } }));
  const offboard = () =>
    run(async () => {
      setConfirmOffboard(false);
      await api(`/api/admin/guilds/${guild.id}`, { method: "DELETE" });
    });

  return (
    <li className="space-y-2 rounded-md border border-ink-line bg-ink/50 p-3">
      <div className="flex items-center gap-3">
        <GuildIcon guild={guild} className="size-8" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-parchment">{guild.name}</span>
        {!guild.botPresent ? (
          <span className="inline-flex items-center gap-1 text-xs text-evil">
            <TriangleAlert className="size-3.5" aria-hidden="true" /> Bot missing
          </span>
        ) : onboarded ? (
          <span className="rounded-full border border-good/50 bg-good/10 px-2 py-0.5 text-[11px] uppercase tracking-wider text-good">
            Active
          </span>
        ) : (
          <span className="text-xs text-parchment-dim">Not onboarded</span>
        )}
      </div>

      {guild.botPresent && (
        <div className="flex items-center gap-2">
          <label htmlFor={`role-${guild.id}`} className="shrink-0 text-xs text-parchment-dim">
            Storyteller role
          </label>
          <select
            id={`role-${guild.id}`}
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            disabled={!roles}
            className={selectClass}
          >
            <option value="">{roles ? "Pick a role" : "Loading…"}</option>
            {roles?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="primary"
            disabled={busy || !roleId || roleId === guild.storytellerRoleId}
            onClick={() => void save()}
          >
            {onboarded ? "Save" : "Onboard"}
          </Button>
        </div>
      )}

      {onboarded && (
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmOffboard(true)}>
            Offboard
          </Button>
        </div>
      )}
      {!guild.botPresent && (
        <p className="text-xs text-parchment-dim">
          The bot has been removed from this server. Add it back with the invite link, or offboard the server.
        </p>
      )}
      {error && <p className="text-xs text-evil">{error}</p>}

      <Dialog open={confirmOffboard} onOpenChange={setConfirmOffboard}>
        <DialogContent title={`Offboard ${guild.name}?`} className="w-[min(26rem,calc(100vw-2rem))]">
          <p className="text-sm text-parchment-dim">
            Its members can no longer sign in unless they&apos;re in another onboarded server, and any game running
            there is deleted without recording history. Finished games stay in everyone&apos;s history.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOffboard(false)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void offboard()}>
              Offboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </li>
  );
}

export function AdminPage() {
  const isAdmin = useAuth((s) => s.isAdmin);
  const refreshSession = useAuth((s) => s.init);
  const [data, setData] = useState<{ inviteUrl: string; guilds: AdminGuild[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api<{ inviteUrl: string; guilds: AdminGuild[] }>("/api/admin/guilds"));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load servers.");
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void load();
    // Coming back from Discord's invite page is the moment a new server appears.
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isAdmin, load]);

  /** After onboarding changes, your own server list (and the host dropdown) changes too. */
  const changed = async (): Promise<void> => {
    await load();
    await refreshSession();
  };

  return (
    <PageShell wide>
      <div className="space-y-4 rounded-xl border border-brass/25 bg-ink-soft/75 p-5 shadow-2xl backdrop-blur-md">
        <div className="flex items-center gap-2">
          <Link to="/" aria-label="Back" className="text-parchment-dim hover:text-parchment">
            <ArrowLeft className="size-4" />
          </Link>
          <h1 className="font-serif text-xl font-semibold text-parchment">Discord servers</h1>
        </div>

        {!isAdmin ? (
          <p className="text-sm text-parchment-dim">Only an admin can onboard servers.</p>
        ) : (
          <>
            <div className="space-y-2 rounded-md border border-ink-line bg-ink/50 p-3">
              <p className="text-sm text-parchment">Add the bot to a server</p>
              <p className="text-xs text-parchment-dim">
                You need Manage Server there. It joins with Administrator, then appears below to onboard.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!data}
                  onClick={() => data && window.open(data.inviteUrl, "_blank", "noopener")}
                >
                  <ExternalLink className="size-3.5" /> Invite bot
                </Button>
                <Button
                  size="sm"
                  disabled={!data}
                  onClick={() => {
                    if (!data) return;
                    void navigator.clipboard.writeText(data.inviteUrl).then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    });
                  }}
                >
                  <Copy className="size-3.5" /> {copied ? "Copied" : "Copy link"}
                </Button>
              </div>
            </div>

            {error && <p className="text-xs text-evil">{error}</p>}
            {data && data.guilds.length === 0 && (
              <p className="text-sm text-parchment-dim">The bot isn&apos;t in any servers yet.</p>
            )}
            <ul className="space-y-2">
              {data?.guilds.map((g) => (
                <GuildRow key={g.id} guild={g} onChanged={changed} />
              ))}
            </ul>
          </>
        )}
      </div>
    </PageShell>
  );
}
